import { supabase } from "./supabaseClient";

export const CMS_STORAGE_BUCKET = "cms-assets";

export interface AktualitaItem {
  id: string;
  nadpis: string;
  text: string;
  datum: string | null;
  obrazekUrl: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface DenniNabidkaContent {
  id: number;
  obrazekUrl: string | null;
  updatedAt?: string;
}

export interface BistroNwtContent {
  id: number;
  textRablinu: string;
  obrazekRablinuUrl: string | null;
  pdfRablinuUrl: string | null;
  textNWT: string;
  obrazekNWTUrl: string | null;
  pdfNWTUrl: string | null;
  updatedAt?: string;
}

export interface CenikRadek {
  popis: string;
  noci2: string;
  noci3: string;
  noci4: string;
}

export interface BalicekContent {
  id: number;
  nadpis: string;
  podnadpis: string;
  uvodniText: string;
  obsahBalicku: string[];
  cenik: CenikRadek[];
  dodatek: string;
  obrazekMobilniUrl: string | null;
  obrazekDesktop1Url: string | null;
  obrazekDesktop2Url: string | null;
  updatedAt?: string;
}

const SINGLETON_ID = 1;

function assertSupabaseConfig() {
  if (!supabase) {
    throw new Error("Supabase klient není inicializovaný.");
  }
}

function normalizeAktualita(row: any): AktualitaItem {
  return {
    id: row.id,
    nadpis: row.nadpis ?? "",
    text: row.text ?? "",
    datum: row.datum ?? null,
    obrazekUrl: row.obrazek_url ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function normalizeBalicek(row: any): BalicekContent {
  return {
    id: row.id ?? SINGLETON_ID,
    nadpis: row.nadpis ?? "",
    podnadpis: row.podnadpis ?? "",
    uvodniText: row.uvodni_text ?? "",
    obsahBalicku: Array.isArray(row.obsah_balicku) ? row.obsah_balicku : [],
    cenik: Array.isArray(row.cenik) ? row.cenik : [],
    dodatek: row.dodatek ?? "",
    obrazekMobilniUrl: row.obrazek_mobilni_url ?? null,
    obrazekDesktop1Url: row.obrazek_desktop1_url ?? null,
    obrazekDesktop2Url: row.obrazek_desktop2_url ?? null,
    updatedAt: row.updated_at,
  };
}

export async function fetchAktuality(limit?: number) {
  assertSupabaseConfig();
  let query = supabase
    .from("cms_aktuality")
    .select("*")
    .order("datum", { ascending: false, nullsFirst: false })
    .order("created_at", { ascending: false });

  if (limit) {
    query = query.limit(limit);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []).map(normalizeAktualita);
}

export async function fetchDenniNabidka() {
  assertSupabaseConfig();
  const { data, error } = await supabase
    .from("cms_denni_nabidka")
    .select("*")
    .eq("id", SINGLETON_ID)
    .maybeSingle();

  if (error) throw error;

  return {
    id: data?.id ?? SINGLETON_ID,
    obrazekUrl: data?.obrazek_url ?? null,
    updatedAt: data?.updated_at,
  } satisfies DenniNabidkaContent;
}

export async function fetchBistroNwt() {
  assertSupabaseConfig();
  const { data, error } = await supabase
    .from("cms_bistro_nwt")
    .select("*")
    .eq("id", SINGLETON_ID)
    .maybeSingle();

  if (error) throw error;

  return {
    id: data?.id ?? SINGLETON_ID,
    textRablinu: data?.text_rablinu ?? "",
    obrazekRablinuUrl: data?.obrazek_rablinu_url ?? null,
    pdfRablinuUrl: data?.pdf_rablinu_url ?? null,
    textNWT: data?.text_nwt ?? "",
    obrazekNWTUrl: data?.obrazek_nwt_url ?? null,
    pdfNWTUrl: data?.pdf_nwt_url ?? null,
    updatedAt: data?.updated_at,
  } satisfies BistroNwtContent;
}

export async function fetchBalicek() {
  assertSupabaseConfig();
  const { data, error } = await supabase
    .from("cms_balicky")
    .select("*")
    .eq("id", SINGLETON_ID)
    .maybeSingle();

  if (error) throw error;
  return normalizeBalicek(data ?? { id: SINGLETON_ID });
}

export async function upsertAktualita(payload: Partial<AktualitaItem>) {
  assertSupabaseConfig();
  const row = {
    id: payload.id,
    nadpis: payload.nadpis ?? "",
    text: payload.text ?? "",
    datum: payload.datum ?? null,
    obrazek_url: payload.obrazekUrl ?? null,
  };

  const { data, error } = await supabase
    .from("cms_aktuality")
    .upsert(row)
    .select("*")
    .single();

  if (error) throw error;
  return normalizeAktualita(data);
}

export async function deleteAktualita(id: string) {
  assertSupabaseConfig();
  const { error } = await supabase.from("cms_aktuality").delete().eq("id", id);
  if (error) throw error;
}

export async function saveDenniNabidka(payload: DenniNabidkaContent) {
  assertSupabaseConfig();
  const { error } = await supabase.from("cms_denni_nabidka").upsert({
    id: SINGLETON_ID,
    obrazek_url: payload.obrazekUrl ?? null,
  });

  if (error) throw error;
}

export async function saveBistroNwt(payload: BistroNwtContent) {
  assertSupabaseConfig();
  const { error } = await supabase.from("cms_bistro_nwt").upsert({
    id: SINGLETON_ID,
    text_rablinu: payload.textRablinu ?? "",
    obrazek_rablinu_url: payload.obrazekRablinuUrl ?? null,
    pdf_rablinu_url: payload.pdfRablinuUrl ?? null,
    text_nwt: payload.textNWT ?? "",
    obrazek_nwt_url: payload.obrazekNWTUrl ?? null,
    pdf_nwt_url: payload.pdfNWTUrl ?? null,
  });

  if (error) throw error;
}

export async function saveBalicek(payload: BalicekContent) {
  assertSupabaseConfig();
  const { error } = await supabase.from("cms_balicky").upsert({
    id: SINGLETON_ID,
    nadpis: payload.nadpis ?? "",
    podnadpis: payload.podnadpis ?? "",
    uvodni_text: payload.uvodniText ?? "",
    obsah_balicku: payload.obsahBalicku ?? [],
    cenik: payload.cenik ?? [],
    dodatek: payload.dodatek ?? "",
    obrazek_mobilni_url: payload.obrazekMobilniUrl ?? null,
    obrazek_desktop1_url: payload.obrazekDesktop1Url ?? null,
    obrazek_desktop2_url: payload.obrazekDesktop2Url ?? null,
  });

  if (error) throw error;
}

function sanitizeFileName(fileName: string) {
  return fileName
    .toLowerCase()
    .replace(/[^a-z0-9.\-_]+/g, "-")
    .replace(/-+/g, "-");
}

export async function uploadCmsAsset(file: File, folder: string) {
  assertSupabaseConfig();
  const filePath = `${folder}/${Date.now()}-${sanitizeFileName(file.name)}`;

  const { error: uploadError } = await supabase.storage
    .from(CMS_STORAGE_BUCKET)
    .upload(filePath, file, {
      cacheControl: "3600",
      upsert: false,
    });

  if (uploadError) throw uploadError;

  const { data } = supabase.storage.from(CMS_STORAGE_BUCKET).getPublicUrl(filePath);
  return data.publicUrl;
}
